import type { BoardColumn, BoardSummary, TaskSummary } from '@obras/shared';
import { daysBetween, formatDate, TASK_URGENCY_LABELS } from '@obras/shared';
import { CalendarClock, GripVertical, ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

import { Avatar } from '../../components/ui/Feedback.js';
import { cn } from '../../lib/cn.js';
import { FLAG_BG, isSignalledUrgency, URGENCY_STYLE } from './styles.js';

const ICON = { strokeWidth: 1.5 } as const;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Prazo em vermelho quando venceu, em âmbar quando vence nos próximos dias. */
function dueTone(dueAt: string): string {
  const days = daysBetween(today(), dueAt);
  if (days < 0) return 'text-danger';
  if (days <= 2) return 'text-warning';
  return 'text-muted-foreground';
}

/**
 * Cartão da tarefa.
 *
 * O corpo é a alça do arrasto; abrir e editar são botões, no rodapé. Enquanto
 * o cartão inteiro também abria a tarefa, todo arrasto que terminava perto de
 * onde começou virava um clique e escancarava o modal sem ninguém pedir.
 *
 * A faixa de cor no alto é escolha de quem cria, sem significado de sistema. A
 * urgência, ao lado do título, só aparece quando é diferente de normal.
 */
function TaskCard({
  task,
  onOpen,
  onEdit,
  onDragStart,
  onDragEnd,
  dragging,
}: {
  task: TaskSummary;
  onOpen: () => void;
  onEdit: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  dragging: boolean;
}) {
  const done = task.checklist.filter((item) => item.isDone).length;
  const urgency = isSignalledUrgency(task.urgency) ? URGENCY_STYLE[task.urgency] : null;
  const UrgencyIcon = urgency?.icon;

  return (
    <article
      draggable
      data-dragging={dragging ? 'true' : undefined}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        // Sem dado no arrasto o Firefox nem inicia o gesto.
        event.dataTransfer.setData('text/plain', task.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={cn(
        'relative cursor-grab overflow-hidden rounded-lg bg-card transition-opacity active:cursor-grabbing',
        dragging && 'opacity-40',
      )}
    >
      {task.color ? (
        <span className={cn('block h-1 w-full', FLAG_BG[task.color])} aria-hidden />
      ) : null}

      <div className="p-3">
        <div className="flex items-start gap-2">
          <GripVertical
            size={14}
            strokeWidth={1.5}
            className="mt-px shrink-0 text-muted-foreground/60"
            aria-hidden
          />

          <h4 className="min-w-0 flex-1 text-[13px] leading-snug font-medium">{task.title}</h4>

          {urgency && UrgencyIcon ? (
            <UrgencyIcon
              size={14}
              strokeWidth={1.75}
              className={cn('mt-px shrink-0', urgency.className)}
              aria-label={`Urgência ${TASK_URGENCY_LABELS[task.urgency].toLowerCase()}`}
            />
          ) : null}
        </div>

        {task.description ? (
          <p className="mt-1.5 line-clamp-2 pl-6 text-[12px] leading-snug text-muted-foreground">
            {task.description}
          </p>
        ) : null}

        {task.dueAt || task.checklist.length > 0 || task.participants.length > 0 ? (
          <div className="mt-2.5 flex items-center gap-3 pl-6 text-[12px] text-muted-foreground">
            {task.dueAt ? (
              <span className={cn('flex items-center gap-1', dueTone(task.dueAt))}>
                <CalendarClock {...ICON} size={13} aria-hidden />
                <span className="tabular">{formatDate(task.dueAt)}</span>
              </span>
            ) : null}

            {task.checklist.length > 0 ? (
              <span className="flex items-center gap-1">
                <ListChecks {...ICON} size={13} aria-hidden />
                <span className="tabular">
                  {done}/{task.checklist.length}
                </span>
              </span>
            ) : null}

            {task.participants.length > 0 ? (
              <span className="ml-auto flex items-center -space-x-1.5">
                {task.participants.slice(0, 3).map((person) => (
                  <Avatar key={person.id} name={person.name} url={person.avatarUrl} size={20} />
                ))}
                {task.participants.length > 3 ? (
                  <span className="tabular grid size-5 place-items-center rounded-full bg-muted text-[10px] font-medium">
                    +{task.participants.length - 3}
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Rodapé de ação: abrir ocupa a largura toda, editar fica no canto. */}
      <div className="flex items-stretch gap-px bg-muted/60">
        <button
          type="button"
          onClick={onOpen}
          className="flex-1 py-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          Ver tarefa
        </button>

        {task.canManage ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Editar a tarefa ${task.title}`}
            title="Editar tarefa"
            className="grid w-10 place-items-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Pencil size={13} strokeWidth={1.75} aria-hidden />
          </button>
        ) : null}
      </div>
    </article>
  );
}

/**
 * Em que posição o cartão cairia, dado onde o ponteiro está.
 *
 * A conta ignora o cartão que está sendo arrastado: dentro da mesma coluna, a
 * lista de destino é a lista sem ele, e contá-lo deslocaria tudo em um ao
 * mover para baixo.
 */
function dropIndex(list: HTMLElement, clientY: number): number {
  const cards = [...list.querySelectorAll<HTMLElement>('[data-card]')].filter(
    (card) => card.dataset['dragging'] !== 'true',
  );

  for (const [index, card] of cards.entries()) {
    const box = card.getBoundingClientRect();
    if (clientY < box.top + box.height / 2) return index;
  }

  return cards.length;
}

function Column({
  column,
  tasks,
  onOpenTask,
  onEditTask,
  onAdd,
  onMove,
  onRemoveColumn,
  dragging,
  setDragging,
}: {
  column: BoardColumn;
  tasks: readonly TaskSummary[];
  onOpenTask: (task: TaskSummary) => void;
  onEditTask: (task: TaskSummary) => void;
  onAdd: () => void;
  onMove: (taskId: string, columnId: string, position: number) => void;
  onRemoveColumn: (() => void) | null;
  dragging: string | null;
  setDragging: (id: string | null) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [insertAt, setInsertAt] = useState<number | null>(null);

  return (
    <section
      onDragOver={(event) => {
        if (!dragging) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (listRef.current) setInsertAt(dropIndex(listRef.current, event.clientY));
      }}
      /*
       * `dragleave` dispara também ao passar de um filho para outro. Comparar
       * com `relatedTarget` distingue "saiu da coluna" de "andou dentro dela" —
       * sem isso o traço de inserção pisca a cada milímetro.
       */
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setInsertAt(null);
      }}
      onDrop={(event) => {
        event.preventDefault();

        const taskId = event.dataTransfer.getData('text/plain') || dragging;
        const position = insertAt ?? tasks.length;

        setInsertAt(null);
        setDragging(null);

        if (taskId) onMove(taskId, column.id, position);
      }}
      className={cn(
        'flex w-72 shrink-0 flex-col rounded-lg bg-muted transition-colors duration-100',
        insertAt !== null && 'bg-accent',
      )}
    >
      <header className="flex items-center gap-2 px-3.5 pt-3.5 pb-2">
        <h3 className="text-[11px] font-medium tracking-[0.06em] uppercase">{column.name}</h3>
        <span className="tabular text-[12px] text-muted-foreground">{tasks.length}</span>

        <div className="ml-auto flex items-center gap-0.5">
          {onRemoveColumn ? (
            <button
              type="button"
              onClick={onRemoveColumn}
              aria-label={`Remover a coluna ${column.name}`}
              className="grid size-6 place-items-center rounded-xs text-muted-foreground transition-colors hover:bg-card hover:text-danger"
            >
              <Trash2 size={13} strokeWidth={1.75} aria-hidden />
            </button>
          ) : null}

          <button
            type="button"
            onClick={onAdd}
            aria-label={`Nova tarefa em ${column.name}`}
            className="grid size-6 place-items-center rounded-xs text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
          >
            <Plus size={15} strokeWidth={1.75} aria-hidden />
          </button>
        </div>
      </header>

      <div ref={listRef} className="flex min-h-20 flex-1 flex-col gap-2 px-2 pb-2">
        {tasks.map((task, index) => (
          <div
            key={task.id}
            data-card
            data-dragging={dragging === task.id ? 'true' : undefined}
            className="relative"
          >
            {insertAt === index ? <DropLine /> : null}
            <TaskCard
              task={task}
              dragging={dragging === task.id}
              onOpen={() => onOpenTask(task)}
              onEdit={() => onEditTask(task)}
              onDragStart={() => setDragging(task.id)}
              onDragEnd={() => {
                setDragging(null);
                setInsertAt(null);
              }}
            />
          </div>
        ))}

        {insertAt !== null && insertAt >= tasks.length ? (
          <div className="relative h-0.5">
            <DropLine />
          </div>
        ) : null}

        {tasks.length === 0 && insertAt === null ? (
          <p className="px-1.5 py-3 text-[13px] text-muted-foreground">Nada aqui.</p>
        ) : null}
      </div>
    </section>
  );
}

/** Traço fino de onde o cartão vai entrar. Ocupa zero altura, não empurra nada. */
function DropLine() {
  return (
    <span aria-hidden className="absolute -top-1 right-0 left-0 h-0.5 rounded-full bg-foreground" />
  );
}

/**
 * Coluna nova sem sair do quadro: o campo é a própria coluna, em rascunho.
 *
 * Fechado é só o "+": ao lado das colunas o sinal já diz o que faz, e escrever
 * "nova coluna" ali dava a um comando raro o mesmo peso visual do trabalho.
 */
function AddColumn({ onAdd }: { onAdd: (name: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  function submit(): void {
    const trimmed = name.trim();
    if (trimmed) onAdd(trimmed);
    setName('');
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Nova coluna"
        title="Nova coluna"
        className="grid size-10 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Plus {...ICON} size={16} aria-hidden />
      </button>
    );
  }

  return (
    <div className="w-56 shrink-0 rounded-lg bg-muted p-2">
      <input
        autoFocus
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={submit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') submit();
          if (event.key === 'Escape') {
            setName('');
            setOpen(false);
          }
        }}
        placeholder="Nome da coluna"
        aria-label="Nome da nova coluna"
        className="h-9 w-full rounded-sm bg-card px-2.5 text-[13px]"
      />
    </div>
  );
}

/**
 * Quadro Kanban.
 *
 * Arrastar usa o mecanismo nativo do navegador, sem biblioteca. O identificador
 * da tarefa viaja pelo `dataTransfer`, não pelo estado do React: a atualização
 * do `dragstart` pode não estar comprometida quando a soltura chega. Quem não
 * arrasta — teclado, toque — muda a coluna pelo campo dentro da tarefa.
 */
export function KanbanBoard({
  board,
  tasks,
  onOpenTask,
  onEditTask,
  onAdd,
  onMove,
  onRemoveColumn,
  onAddColumn,
}: {
  board: BoardSummary;
  tasks: readonly TaskSummary[];
  onOpenTask: (task: TaskSummary) => void;
  onEditTask: (task: TaskSummary) => void;
  onAdd: (columnId: string) => void;
  onMove: (taskId: string, columnId: string, position: number) => void;
  onRemoveColumn: (columnId: string) => void;
  onAddColumn: (name: string) => void;
}) {
  const [dragging, setDragging] = useState<string | null>(null);

  const byColumn = (columnId: string): TaskSummary[] =>
    tasks
      .filter((task) => (board.isPersonal ? task.stage === columnId : task.columnId === columnId))
      .sort((a, b) => a.position - b.position);

  /*
   * As colunas se esticam até o pé da janela mesmo com poucos cartões: coluna
   * curta sobre uma faixa branca larga faz o quadro parecer quebrado, e a área
   * de soltura ficava restrita à altura dos cartões que já existiam.
   */
  return (
    <div className="flex min-h-[calc(100dvh-19rem)] items-stretch gap-3 overflow-x-auto pb-2">
      {board.columns.map((column) => (
        <Column
          key={column.id}
          column={column}
          tasks={byColumn(column.id)}
          onOpenTask={onOpenTask}
          onEditTask={onEditTask}
          onAdd={() => onAdd(column.id)}
          onMove={onMove}
          onRemoveColumn={
            board.canManage && board.columns.length > 1 ? () => onRemoveColumn(column.id) : null
          }
          dragging={dragging}
          setDragging={setDragging}
        />
      ))}

      {board.canManage ? (
        <div className="self-start">
          <AddColumn onAdd={onAddColumn} />
        </div>
      ) : null}
    </div>
  );
}
